/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import { useEffect, useRef } from 'react'

import ClipboardJS from 'clipboard'
import { toast } from 'react-toastify'
import styled, { css } from 'styled-components'

import { ERROR_MESSAGE } from 'src/constants/errors'
import { pxToVw } from 'src/functions/units'

import { useThemeContext } from '../../contexts/ThemeContext'
import { BaseButton } from '../Button'
import { ToastContent } from '../Common/Toast'
import Icon from '../Icon'

type Props = {
  text: string
  onCopiedMessage?: string
}

const CopyButton = ({ text, onCopiedMessage }: Props) => {
  const { colors } = useThemeContext()
  const ref = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    let clipboard: ClipboardJS

    if (ref.current) {
      clipboard = new ClipboardJS(ref.current, {
        text: function () {
          return text
        }
      })

      clipboard.on('success', function (e) {
        toast(<ToastContent message={onCopiedMessage || 'Text copied to clipboard'} />, {
          progressStyle: {
            background: colors.primary
          }
        })

        e.clearSelection()
      })

      clipboard.on('error', function (e) {
        toast.error(<ToastContent message={ERROR_MESSAGE.CLIPBOARD_FAILED} />)
      })
    }

    return () => {
      if (clipboard) {
        clipboard.destroy()
      }
    }
  }, [text, onCopiedMessage, colors.primary])

  return (
    <StyledCopyButton ref={ref}>
      <Icon icon="copy" size={19} color={colors.primary600} />
    </StyledCopyButton>
  )
}

export default CopyButton

const StyledCopyButton = styled(BaseButton)`
  padding: 4px;
  height: fit-content;
  display: inline-flex;
  cursor: pointer;

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      padding: ${pxToVw(4, 720)};
    `}
  `}
`
